import { useState } from "react";

import { Controller, useForm } from "react-hook-form";

import { RoomType, type PatchBuildingRoomsRequestBody } from "@eisox/api/core-api";
import { ActionButton, Alert, cn, Select, Slider, Steps } from "@eisox/design-system";
import { isFeatureSupported } from "@eisox/device-features";
import { ArrowRightIcon } from "@eisox/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { roomApi } from "~/api";
import { FieldContainer } from "~/components/field-container";
import { TEMPERATURE_MAX, TEMPERATURE_MIN, TEMPERATURE_STEP } from "~/constants/temperature.constant";
import { frostFreePeriodsAllowed } from "~/features/frost-free";
import {
  getComfortTemperature,
  getNightTemperature,
  getType,
  RoomsSelector,
  type Filters,
  type RoomsTemperaturesSettingsSchemaType,
} from "~/features/settings";
import { AvailableRoomTypes } from "~/helpers/room.helper";
import { clampTemperature, getRoomsTemperatureBounds } from "~/helpers/temperature.helper";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import type { EnhancedRoom } from "~/types/room.type";
import { getDirtyValues } from "~/utils/form.utils";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/rooms/type-temp")({
  component: RouteComponent,
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const { building, floors, rooms, valves, gateways } = useBuildingContext(Route.id);

  const { mutate, isPending } = useMutation({
    mutationFn: (body: PatchBuildingRoomsRequestBody) => roomApi.update(building.id, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries(roomApi.queries.building(building.id));
      setFilters({});
      setRoomIds([]);
      reset(defaultValues);
    },
  });

  const [filters, setFilters] = useState<Filters>({});
  const [blured, setBlured] = useState(false);
  const [roomIds, setRoomIds] = useState<string[]>([]);

  const selectedRooms = rooms.filter(r => roomIds.includes(r.id));

  const defaultValues = {
    roomType: undefined,
    comfortTemperature: undefined,
    nightTemperature: undefined,
  };

  const {
    control,
    watch,
    formState: { isDirty, dirtyFields, errors },
    handleSubmit,
    reset,
  } = useForm<RoomsTemperaturesSettingsSchemaType>({
    defaultValues,
  });

  const type = watch("type");
  const { comfortTemperatureBounds, nightTemperatureBounds } = getRoomsTemperatureBounds({
    absenceTemperature: building.absenceTemperature,
    preComfortTemperature: building.preComfortTemperature,
  });

  const onSubmit = (data: RoomsTemperaturesSettingsSchemaType) => {
    const dirtyValues = getDirtyValues(dirtyFields, data);
    const body: PatchBuildingRoomsRequestBody[number] = {
      ids: roomIds,
      ...(dirtyValues.type != null && { roomType: dirtyValues.type }),
      ...(dirtyValues.comfortTemperature != null && { comfortTemperature: dirtyValues.comfortTemperature }),
      ...(dirtyValues.nightTemperature != null && { nightTemperature: dirtyValues.nightTemperature }),
    };
    mutate([body]);
  };

  const handleFilterRoomsChange = (rooms: EnhancedRoom[]) => {
    const roomIds = rooms.map(room => room.id);
    setRoomIds(roomIds);
    reset({
      type: getType(rooms),
      comfortTemperature: getComfortTemperature(rooms),
      nightTemperature: getNightTemperature(rooms),
    });
  };

  const inputsDisabled = roomIds.length === 0;
  const submitDisabled = !isDirty || inputsDisabled || isPending;

  const showSoundSensorFilter = rooms.some(room => {
    const roomValves = valves.filter(v => v.roomId === room.id);
    const roomGatewayIds = [...new Set(roomValves.map(v => v.gatewayId))];
    const roomGateways = gateways.filter(g => roomGatewayIds.includes(g.id));
    return (
      roomValves.length > 0 &&
      roomValves.every(v => isFeatureSupported("valve", "SOUND_SENSOR_PARAM", v.softwareVersion)) &&
      roomGateways.length > 0 &&
      roomGateways.every(g => isFeatureSupported("gateway", "SOUND_SENSOR_PARAM", g.softwareVersion))
    );
  });

  const showCovSensorFilter = rooms.some(room => {
    const roomValves = valves.filter(v => v.roomId === room.id);
    const roomGatewayIds = [...new Set(roomValves.map(v => v.gatewayId))];
    const roomGateways = gateways.filter(g => roomGatewayIds.includes(g.id));
    return (
      roomValves.length > 0 &&
      roomValves.every(v => isFeatureSupported("valve", "COV_SENSOR_PARAM", v.softwareVersion)) &&
      roomGateways.length > 0 &&
      roomGateways.every(g => isFeatureSupported("gateway", "COV_SENSOR_PARAM", g.softwareVersion))
    );
  });

  const isFrostFreePeriodsAllowed = frostFreePeriodsAllowed(gateways);

  const hasNightTemperature =
    type !== undefined ? type === RoomType.ROOM : selectedRooms.some(r => r.type === RoomType.ROOM);

  return (
    <>
      <form className="flex h-full flex-col overflow-y-auto" onSubmit={e => void handleSubmit(onSubmit)(e)}>
        <Steps.Root>
          <Steps.Step>
            <Steps.Title>{m.select_places({ resource: "room" })}</Steps.Title>
            <Steps.Content>
              <FieldContainer classNames={{ container: "w-full" }}>
                <RoomsSelector.Root
                  rooms={rooms}
                  value={filters}
                  onValueChange={setFilters}
                  onFilteredRoomsChange={handleFilterRoomsChange}
                >
                  <div className="*:min-w-0 mb-[5px] flex gap-[5px] *:flex-1">
                    <RoomsSelector.TypeSearch />
                    <RoomsSelector.PlanSearch floors={floors} />
                    <RoomsSelector.GroupSearch />
                    <RoomsSelector.ParametersSearch
                      showSoundSensorFilter={showSoundSensorFilter}
                      showCovSensorFilter={showCovSensorFilter}
                      frostFreePeriodsAllowed={isFrostFreePeriodsAllowed}
                    />
                    <RoomsSelector.PersonnalizedSearch floors={floors} />
                  </div>
                  <RoomsSelector.Result />
                </RoomsSelector.Root>
              </FieldContainer>
            </Steps.Content>
          </Steps.Step>
          <Steps.Step>
            <Steps.Title>{m.update_settings()}</Steps.Title>
            <Steps.Content className="flex flex-col gap-[10px]">
              <div className={cn("flex max-w-[420px] flex-col gap-[5px]", blured && !dirtyFields.type && "blur-[3px]")}>
                <Controller
                  control={control}
                  name="type"
                  render={({ field: { value, onChange } }) => (
                    <FieldContainer
                      classNames={{
                        label: "text-primary-blue font-semibold text-base leading-[21px]",
                      }}
                      label={m.type()}
                    >
                      <Select.Root multiple={false} value={value} onValueChange={onChange}>
                        <Select.Trigger disabled={inputsDisabled}>
                          <Select.Value placeholder={m.type()} />
                        </Select.Trigger>
                        <Select.Content>
                          <Select.Viewport>
                            {AvailableRoomTypes.map(type => (
                              <Select.Item key={type} value={type}>
                                {m.room_type({ type })}
                              </Select.Item>
                            ))}
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Root>
                    </FieldContainer>
                  )}
                />
              </div>
              <div className="flex max-w-[420px] flex-col gap-[5px]">
                <h3 className="font-semibold text-base leading-[21px] text-primary-blue">{m.temperatures()}</h3>
                <Controller
                  control={control}
                  name="comfortTemperature"
                  render={({ field: { value, onChange } }) => (
                    <>
                      <FieldContainer
                        error={errors.comfortTemperature}
                        classNames={{
                          container: blured && !dirtyFields.comfortTemperature ? "blur-[3px]" : undefined,
                        }}
                      >
                        <Slider
                          label={`${m.comfort()} (°C)`}
                          value={value}
                          min={TEMPERATURE_MIN}
                          max={TEMPERATURE_MAX}
                          step={TEMPERATURE_STEP}
                          valueLabelDisplay="auto"
                          valueLabelFormat={`${comfortTemperatureBounds.min - TEMPERATURE_STEP}°C (${m.pre_comfort()}) - ${value}°C (${m.comfort()})`}
                          onValueChange={nextValue => {
                            if (nextValue === undefined) return;
                            onChange(clampTemperature(nextValue, comfortTemperatureBounds));
                          }}
                          disabled={inputsDisabled}
                        />
                      </FieldContainer>
                      {value === undefined && (
                        <p className="font-normal text-base italic">{m.no_uniform_temperature()}</p>
                      )}
                    </>
                  )}
                />
                {hasNightTemperature && (
                  <Controller
                    control={control}
                    name="nightTemperature"
                    render={({ field: { value, onChange } }) => (
                      <>
                        <FieldContainer
                          classNames={{
                            container: blured && !dirtyFields.nightTemperature ? "blur-[3px]" : undefined,
                          }}
                        >
                          <Slider
                            label={`${m.night()} (°C)`}
                            value={value}
                            min={TEMPERATURE_MIN}
                            max={TEMPERATURE_MAX}
                            step={TEMPERATURE_STEP}
                            valueLabelDisplay="auto"
                            valueLabelFormat={`${nightTemperatureBounds.min - TEMPERATURE_STEP}°C (${m.pre_comfort()}) - ${value}°C (${m.night()})`}
                            onValueChange={newValue => {
                              if (newValue === undefined) return;
                              onChange(clampTemperature(newValue, nightTemperatureBounds));
                            }}
                            disabled={inputsDisabled}
                          />
                        </FieldContainer>
                        {value === undefined && (
                          <p className="font-normal text-base italic">{m.no_uniform_temperature()}</p>
                        )}
                      </>
                    )}
                  />
                )}
              </div>
            </Steps.Content>
          </Steps.Step>
          <Steps.Step>
            <Steps.Title>{m.apply_parameters()}</Steps.Title>
            <Steps.Content className="flex flex-col items-start gap-[10px]">
              {rooms.length === roomIds.length && isDirty && (
                <Alert severity="warning">{m.updates_applied_to_all_rooms()}</Alert>
              )}
              <ActionButton
                type="submit"
                disabled={submitDisabled}
                onMouseEnter={() => setBlured(isDirty)}
                onMouseLeave={() => setBlured(false)}
              >
                {m.save()}
                <ArrowRightIcon />
              </ActionButton>
            </Steps.Content>
          </Steps.Step>
        </Steps.Root>
      </form>
    </>
  );
}
